import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';
import { Header } from "@/components/header";
import { Footer } from '@/components/footer';
import { CTA } from '@/components/cta';
import { FAQ } from '@/components/faq';

export default function PerformanceBenefit() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-16">        
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-slate-100 dark:bg-slate-900/30 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-600 dark:text-slate-400">
              <Zap className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t('benefitsCloud.benefits.5.title')}
            </h1>
          </div>
          
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
              {t('benefitsCloud.benefits.5.description')}
            </p>
            
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                {t('benefitsCloud.details.performance.title', 'Lightning-Fast Performance')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                {t('benefitsCloud.details.performance.description', 'Experience blazing-fast performance with our optimized cloud infrastructure, ensuring quick response times and smooth operation even during peak usage.')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.performance.benefit1', 'High-Speed Processing')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.performance.benefit1Desc', 'Process security data and analytics in real-time with minimal latency.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.performance.benefit2', 'Optimized Infrastructure')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.performance.benefit2Desc', 'Leverage our high-performance cloud infrastructure for maximum efficiency.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.performance.benefit3', 'Low Latency')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.performance.benefit3Desc', 'Minimize delays in security alerts and system responses.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.performance.benefit4', 'Reliable Uptime')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.performance.benefit4Desc', 'Enjoy 99.9% uptime with our robust infrastructure.')}
                  </p>
                </div>
              </div>
              
              <div className="mt-16">
                <FAQ 
                    faqs={t('benefitsCloud.details.performance.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} 
                    title={t('faq.title', 'Frequently Asked Questions')}
                    subtitle={t('benefitsCloud.details.performance.faqSubtitle', 'Everything you need to know about performance')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CTA />
      <Footer />
    </div>
  );
}
